import { useApolloClient } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { toast } from '@side/pantry';
import { capitalize } from 'lodash';
import { useCallback } from 'react';
import { GQLInteractionType } from 'src/generated/graph-api';
import { isCommunityListingStatus } from '@/components/SideBar/useInteractionStatus/useInteractionStatus.helpers';
import {
  useAddPageBannerToState,
  useClearPageBannerById,
} from '@/state/navigation';
import {
  StatusChangeBannerId,
  StatusChangedBanner,
} from '@/views/Coversheet/components/PageBanner/StatusChangedBanner';
import {
  useCoverSheetLazyQuery,
  useUpdateCoverSheetMutation,
} from '@/views/Coversheet/hooks/coverSheet.generated';
import { coreApi } from '$api-clients/clients';
import type { CoreSchema } from '$api-clients/generated';

type ListingPatchBody =
  CoreSchema.paths['/listings/{listingId}']['patch']['requestBody']['content']['application/json'];
type OfferPatchBody =
  CoreSchema.paths['/offers/{offerId}']['patch']['requestBody']['content']['application/json'];
type LeasePatchBody = Extract<
  CoreSchema.paths['/interactions/{interactionId}']['patch']['requestBody']['content']['application/json'],
  { type: 'lease' }
>['interaction'];

// The status is already valid for the current type (the dropdown is built from
// `getTransactionStatusList`), so the casts are safe. `market` is only needed
// for the community-listing check, which only applies to listings.
async function patchInteractionStatus(
  type: GQLInteractionType,
  id: string,
  status: string,
) {
  if (type === GQLInteractionType.listing) {
    const { data, error } = await coreApi.PATCH('/listings/{listingId}', {
      params: { path: { listingId: id } },
      body: { status: status as ListingPatchBody['status'] },
    });
    return { error, market: data?.property?.state ?? '' };
  }

  if (type === GQLInteractionType.offer) {
    const { error } = await coreApi.PATCH('/offers/{offerId}', {
      params: { path: { offerId: id } },
      body: { status: status as OfferPatchBody['status'] },
    });
    return { error, market: '' };
  }

  if (type === GQLInteractionType.lease) {
    const { error } = await coreApi.PATCH('/interactions/{interactionId}', {
      params: { path: { interactionId: id } },
      body: {
        type: 'lease',
        interaction: { status: status as LeasePatchBody['status'] },
      },
    });
    return { error, market: '' };
  }

  throw new Error(`Unsupported interaction type: ${type}`);
}

export function useUpdateInteractionStatus(
  type: GQLInteractionType,
  id: string,
) {
  const client = useApolloClient();
  const [fetchCoverSheetData] = useCoverSheetLazyQuery();
  const [updateCoverSheet] = useUpdateCoverSheetMutation();
  const addBanner = useAddPageBannerToState();
  const clearBanner = useClearPageBannerById(id);

  return useCallback(
    async (status: string) => {
      try {
        const { error, market } = await patchInteractionStatus(
          type,
          id,
          status,
        );
        if (error !== undefined) {
          throw new Error(typeof error === 'string' ? error : error.message);
        }

        // Mirror the new status into the Apollo cache so still-GraphQL consumers
        // (the interactions list cards) reflect it without a refetch.
        client.cache.modify({
          id: client.cache.identify({ __typename: capitalize(type), id }),
          fields: { displayStatus: () => status },
        });

        // If interaction was opted in to Side Listings and new status no longer
        // qualifies, automatically opt out
        const isValidCommunityListingStatus = isCommunityListingStatus({
          status,
          market,
        });

        if (isValidCommunityListingStatus) {
          clearBanner(StatusChangeBannerId);
          return true;
        }

        const { data: coverSheetData } = await fetchCoverSheetData({
          variables: {
            interactionId: id,
            interactionType: type,
          },
        });
        const { showOnInsideListings, property } =
          coverSheetData?.coverSheet ?? {};

        if (showOnInsideListings) {
          void updateCoverSheet({
            variables: {
              input: {
                interactionId: id,
                interactionType: type,
                entity: 'interaction',
                entityId: null,
                fields: [
                  {
                    name: 'showOnInsideListings',
                    value: String(false),
                  },
                ],
              },
            },
          });
          addBanner(
            {
              id: StatusChangeBannerId,
              banner: (
                <StatusChangedBanner
                  interactionId={id}
                  interactionStatus={status}
                  market={property?.state ?? ''}
                />
              ),
            },
            id,
          );
        }

        return true;
      } catch (err) {
        datadogRum.addError('Failed to update interaction status', {
          err,
          type,
          id,
          status,
        });
        toast('error', 'Failed to update status', {
          showCloseButton: true,
        });
        return false;
      }
    },
    [
      type,
      id,
      client,
      updateCoverSheet,
      addBanner,
      clearBanner,
      fetchCoverSheetData,
    ],
  );
}
